import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";
import { ApiError } from "@/src/libs/ApiError";

/**
 * La fiche d'administration d'une publication.
 *
 * ⚠️ **`GET /moderation/post/{id}`, et surtout pas `GET /post/{id}`.** La route
 * publique applique `canViewPost`, qui ne fait **aucune exception de rôle** :
 * une publication en cours de classement, retenue, refusée ou retirée par son
 * auteur y répond 403 même à un administrateur, et c'est délibéré côté serveur.
 * Or ce sont exactement les publications qu'on vient lire ici.
 *
 * `viewerHasLiked` y vaut toujours faux : un administrateur ne « like » pas
 * depuis la modération.
 */
export function useAdminPost(postId: number) {
  const $api = useApiClient();
  const t = useTranslations("posts");

  const { data, error, isError, isLoading } = $api.useQuery(
    "get",
    "/api/v1/moderation/post/{id}",
    { params: { path: { id: postId } } },
    // ⚠️ **Un identifiant illisible ne part pas au serveur.** `/posts/abc`
    // donne `NaN`, que l'écran ramène à `0` : la route le refuserait en 400
    // `INVALID_REQUEST_DATA`, ce qui ferait surgir un toast de panne sur ce
    // qui n'est qu'une URL mal tapée.
    { enabled: postId > 0 },
  );

  // Un 404 est un cas normal : l'URL a été tapée à la main, ou la publication a
  // été effacée depuis. L'écran le dit lui-même ; un toast ferait croire à une
  // panne du serveur.
  const isMissing = ApiError.from(error)?.status === 404;

  useQueryErrorToast(isError, t("detail-error"), error, !isMissing);

  return { post: data?.data, isLoading, isError, isMissing };
}
